import { useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextareaFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import type { Order, Project } from '../data/databaseSchema';
import { orderService } from '../services/orderService';
import { projectService } from '../services/projectService';
import { formatDateDdMmYyyy } from '../utils/dateUtils';

interface OrderFormData {
  order_number: string;
  project_id: string;
  comments: string;
  order_date: string;
}

interface OrderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (order: Order) => void;
  onEntrySaved?: () => void;
  order?: Order | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const OrderDrawer: React.FC<OrderDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  order,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<OrderFormData>({
    order_number: '',
    project_id: '',
    comments: '',
    order_date: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getProjects(),
    enabled: isOpen && !readOnly,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && order) {
        setFormData({
          order_number: order.order_number || '',
          project_id: order.project_id || '',
          comments: order.comments || '',
          order_date: order.order_date ? order.order_date.split('T')[0] : '',
        });
      } else {
        setFormData({
          order_number: '',
          project_id: '',
          comments: '',
          order_date: '',
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, order]);

  const handleInputChange = (field: keyof OrderFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.order_number || formData.order_number.trim() === '') {
      newErrors.order_number = 'Order number is required';
    }

    if (!formData.project_id) {
      newErrors.project_id = 'Project is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData: Record<string, string> = {
        order_number: formData.order_number.trim(),
        project_id: formData.project_id,
      };

      if (formData.comments?.trim()) {
        apiData.comments = formData.comments.trim();
      }

      if (formData.order_date) {
        try {
          const date = parseISO(`${formData.order_date}T00:00:00Z`);
          if (!Number.isNaN(date.getTime())) {
            apiData.order_date = date.toISOString();
          }
        } catch {
          // If parsing fails, skip adding order_date
        }
      }

      const response =
        isEdit && order
          ? await orderService.updateOrder(order.id, apiData)
          : await orderService.createOrder(apiData);

      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectOptions = (projectsData || []).map((project: Project) => ({
    value: project.id,
    label: `${project.name} (${project.project_number})`,
  }));

  const projectLabel = useMemo(() => {
    if (order?.project) {
      const { name, project_number: number } = order.project;
      return number ? `${name} (${number})` : name || 'N/A';
    }
    return (
      projectOptions.find((option) => option.value === formData.project_id)?.label ||
      formData.project_id ||
      'N/A'
    );
  }, [order?.project, projectOptions, formData.project_id]);

  const orderDateDisplay = formData.order_date
    ? formatDateDdMmYyyy(`${formData.order_date}T00:00:00Z`)
    : 'N/A';

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Order' : isEdit ? 'Edit Order' : 'Add Order'}
      description={
        readOnly
          ? 'Order details.'
          : isEdit
            ? 'Update the order details below.'
            : 'Fill in the details for the new order.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update Order' : 'Add Order'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="Order Number"
          required
          value={formData.order_number}
          onChange={(v) => handleInputChange('order_number', v)}
          error={errors.order_number}
          disabled={isEdit || fieldsDisabled}
          placeholder="Enter order number"
          copyable={readOnly}
        />
        {readOnly ? (
          <TextFormField
            label="Project"
            required
            value={projectLabel}
            onChange={() => {}}
            disabled
            copyable
          />
        ) : (
          <SelectFormField
            label="Project"
            required
            value={formData.project_id}
            onChange={(v) => handleInputChange('project_id', v)}
            options={projectOptions}
            placeholder={isLoadingProjects ? 'Loading projects...' : 'Select a project'}
            disabled={isLoadingProjects}
            error={errors.project_id ?? (projectsError ? 'Failed to load projects' : undefined)}
          />
        )}
        <TextFormField
          label="Order Date"
          type={readOnly ? 'text' : 'date'}
          value={readOnly ? orderDateDisplay : formData.order_date}
          onChange={(v) => handleInputChange('order_date', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextareaFormField
          label="Comments"
          value={formData.comments}
          onChange={(v) => handleInputChange('comments', v)}
          placeholder="Enter comments"
          disabled={fieldsDisabled}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
